import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Check, Copy, Play, Square, Eraser } from "lucide-react";
import { useLogMonitor } from '@/features/LogMonitor/hooks/useLogMonitor';
import { LogLine } from '@/features/LogMonitor/services/logService';
import { LogTerminal, LogTerminalRef } from '@/shared/components/ui/log-terminal';
import { useToast } from '@/shared/hooks/use-toast';
import { AssistantLogsTab } from './AssistantLogsTab';
import { AgenticLogsTab } from './AgenticLogsTab';
import { GatewayLogsTab } from './GatewayLogsTab';
import { EngineLogsTab } from './EngineLogsTab';

export const LogViewer = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    logLines,
    isStreaming,
    isReconnecting,
    startStreaming,
    stopStreaming,
    error,
    clearLogs
  } = useLogMonitor();
  
  // Estado para controlar o botão de copiar
  const [copied, setCopied] = useState(false);

  const logTerminalRef = useRef<LogTerminalRef>(null);
  
  // Função para copiar logs para a área de transferência
  const copyLogs = () => {
    // Pegar apenas o texto dos logs, sem formatação
    const logText = filteredLogs.map(log => log.line).join('\n');
    
    // Copiar para a área de transferência
    navigator.clipboard.writeText(logText)
      .then(() => {
        setCopied(true);
        toast({
          title: t('logs.copied', 'Logs copiados'),
          description: t('logs.copiedDescription', 'Os logs foram copiados para a área de transferência'),
          variant: 'default'
        });
        
        // Resetar o ícone após 2 segundos
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Erro ao copiar logs:', err);
        toast({
          title: t('logs.copyError', 'Erro ao copiar'),
          description: t('logs.copyErrorDescription', 'Não foi possível copiar os logs para a área de transferência'),
          variant: 'destructive'
        });
      });
  };

  // Scroll para o final apenas quando novos logs forem adicionados (respeitando a escolha do usuário)
  useEffect(() => {
    // Garantir que o scroll aconteça após a renderização
    const timeoutId = setTimeout(() => {
      if (logTerminalRef.current) {
        // O método scrollToBottom já verifica shouldAutoScroll internamente
        logTerminalRef.current.scrollToBottom();
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [logLines]);

  // Scroll inicial quando o streaming começar ou for ativado/desativado
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (logTerminalRef.current) {
        // Forçar o scroll inicial, mas respeitar a preferência depois
        logTerminalRef.current.scrollToBottom();
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isStreaming]);

  // Iniciar streaming automaticamente quando o componente montar
  // e forçar uma nova conexão sempre que o componente for montado
  useEffect(() => {
    console.log('[LogViewer] Componente montado, iniciando streaming de logs');
    // Forçar parada e início para garantir uma conexão limpa
    stopStreaming();
    setTimeout(() => {
      startStreaming();
    }, 500);
    
    // Limpar quando o componente desmontar
    return () => {
      console.log('[LogViewer] Componente desmontado, parando streaming de logs');
      stopStreaming();
    };
  }, [startStreaming, stopStreaming]);

  // Verificar novos logs periodicamente (sem forçar scroll)
  useEffect(() => {
    if (!isStreaming) return;
    
    // Intervalo menos frequente para verificar novos logs
    const intervalId = setInterval(() => {
      if (logTerminalRef.current) {
        // O método scrollToBottom já verifica shouldAutoScroll internamente
        logTerminalRef.current.scrollToBottom();
      }
    }, 5000); // Aumentado para 5 segundos para ser menos agressivo
    
    return () => clearInterval(intervalId);
  }, [isStreaming]);

  // Filtrar logs para remover heartbeats e destacar logs da API
  const filteredLogs = logLines.filter(log => {
    // Verificar se a linha existe
    if (!log.line) return false;
    
    // Excluir mensagens de heartbeat
    return !log.line.toLowerCase().includes('[heartbeat]');
  });

  // Formatar a linha de log para exibição
  const formatLogLine = (logLine: LogLine) => {
    if (!logLine.line) return '';

    // Cor por nível, a partir do contrato de tokens. Só o que exige atenção
    // ganha cor — erro e aviso. Linha de informação fica em texto normal, para
    // a tela não virar um bloco saturado em leitura longa.
    let className = 'text-foreground';
    const line = logLine.line.toLowerCase();

    if (line.includes('[api]') || line.includes('/api/') || line.includes('api request')) {
      className = 'text-info-fg font-medium';
    } else if (line.includes('error') || line.includes('exception') || line.includes('fail')) {
      className = 'text-error-fg font-medium';
    } else if (line.includes('warn')) {
      className = 'text-warning-fg';
    } else if (line.includes('debug')) {
      className = 'text-faint';
    }

    return (
      <div className={className}>
        {logLine.timestamp && (
          <span className="text-faint text-xs mr-2">
            [{new Date(logLine.timestamp).toLocaleTimeString()}]
          </span>
        )}
        {logLine.line}
      </div>
    );
  };

  return (
    <Tabs defaultValue="api" className="w-full">
      <TabsList className="mb-4">
        <TabsTrigger value="api">{t('logs.tabs.api', 'API')}</TabsTrigger>
        <TabsTrigger value="assistant">{t('logs.tabs.assistant', 'Assistant')}</TabsTrigger>
        <TabsTrigger value="agentic">{t('logs.tabs.agentic', 'Agentic')}</TabsTrigger>
        <TabsTrigger value="gateway">{t('logs.tabs.gateway', 'Gateway')}</TabsTrigger>
        <TabsTrigger value="engine">{t('logs.tabs.engine', 'Engine')}</TabsTrigger>
      </TabsList>

      <TabsContent value="api">
      <Card className="w-full">
      <CardContent className="pt-6 space-y-4">
        <div className="mb-1">
          <div className="flex items-center space-x-2">
            <span className="text-sm">
              {isStreaming ? (
                isReconnecting ? (
                  <span className="flex items-center text-amber-500">
                    <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse mr-2"></span>
                    {t('logs.reconnecting', 'Reconectando...')}
                  </span>
                ) : (
                  <span className="flex items-center text-green-500">
                    <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></span>
                    {t('logs.streamingActive')}
                  </span>
                )
              ) : (
                <span className="text-muted-foreground">{t('logs.streamingStopped')}</span>
              )}
            </span>
            {filteredLogs.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {t('logs.lineCount', { count: filteredLogs.length })}
                {logLines.length > filteredLogs.length && (
                  <span className="ml-1 text-xs">
                    ({logLines.length - filteredLogs.length} heartbeat messages filtered)
                  </span>
                )}
              </span>
            )}
          </div>
        </div>

        {error && (
          <div className="p-2 text-sm bg-destructive/10 text-destructive rounded border border-destructive/20">
            {error}
          </div>
        )}

        <div className="h-[500px] overflow-hidden">
          {!isStreaming && logLines.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full border rounded-md bg-card text-muted-foreground">
              <p>{t('logs.noLogEntries')}</p>
              <Button
                variant="secondary"
                size="sm"
                onClick={startStreaming}
                className="mt-4"
              >
                <Play className="h-4 w-4 mr-1" />
                {t('settings.logs.startStreaming')}
              </Button>
            </div>
          ) : (
            <LogTerminal 
              className="h-full" 
              ref={logTerminalRef}
              title={t('logs.terminalTitle')}
              actionButtons={
                <div className="flex space-x-3">
                  {isStreaming ? (
                    <button
                      onClick={stopStreaming}
                      className="text-red-500 hover:text-red-700 focus:outline-none"
                      title={t('logs.stopStreaming')}
                      disabled={false}
                    >
                      <Square className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={startStreaming}
                      className="text-green-500 hover:text-green-700 focus:outline-none"
                      title={t('settings.logs.startStreaming')}
                      disabled={false}
                    >
                      <Play className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={clearLogs}
                    className={`text-gray-500 hover:text-gray-700 focus:outline-none ${logLines.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('logs.clearLogs')}
                    disabled={logLines.length === 0}
                  >
                    <Eraser className="h-4 w-4" />
                  </button>
                  <button
                    onClick={copyLogs}
                    className={`text-gray-500 hover:text-gray-700 focus:outline-none ${filteredLogs.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={t('logs.copyLogsTitle', 'Copy Logs')}
                    disabled={filteredLogs.length === 0}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              }
            >
              {filteredLogs.map((line, index) => (
                <div key={index} className="py-0.5 whitespace-pre-wrap break-all">
                  {formatLogLine(line)}
                </div>
              ))}
              {isStreaming ? (
                <div className="h-4 flex items-center mt-1">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></div>
                  <span className="text-xs text-green-600">{t('logs.liveStreaming')}</span>
                </div>
              ) : (
                <div className="h-4 flex items-center mt-1">
                  <div className="h-2 w-2 rounded-full bg-red-500 mr-2"></div>
                  <span className="text-xs text-red-600">{t('logs.monitoringStopped')}</span>
                </div>
              )}
            </LogTerminal>
          )}
        </div>
      </CardContent>
    </Card>
      </TabsContent>

      <TabsContent value="assistant">
        <AssistantLogsTab />
      </TabsContent>

      <TabsContent value="agentic">
        <AgenticLogsTab />
      </TabsContent>

      <TabsContent value="gateway">
        <GatewayLogsTab />
      </TabsContent>

      <TabsContent value="engine">
        <EngineLogsTab />
      </TabsContent>
    </Tabs>
  );
};
